/**
 * @file ElementSpaceField.tsx
 * @description Отступ элемента сверху или снизу числом (phase19.16, партия
 * SB120, этап 3а; uiux-sb120-stage3 §2.1 «Расположение»; данные
 * db-design-sb120-stage3 §2 `space.top/bottom/topPhone/bottomPhone`):
 *
 * ```
 * Отступ сверху                              ↺ обычно
 * ├──●─────────┤ [ 24 ] px   [0][8][16][32][64]
 *  Обычно — только ритм колонки (32 px); своё число добавляется к нему.
 *  на телефоне 14,4 px (60 %)                Своё ›
 * ```
 *
 * На витрине число — `margin` обёртки элемента, он **добавляется** к ритму
 * колонки (`gap`: «Между элементами», иначе M сайта), как и ступени v3.
 * Нет ключа — 0 сверх ритма; число 0–200 px; на телефоне — ПК × доля
 * телефона темы или своё число (`topPhone` / `bottomPhone`). Дроби не
 * округляются (12,8 после перевода ступеней при «Компактно»). Ползунок —
 * живой предпросмотр на канве. Подпись сброса короткая («обычно»), пояснение
 * про ритм — одной строкой подсказки с переносом: панель на 360–1280 не
 * скроллится вбок (QA этапа 3а, D2).
 */

import { SECTION_LOOK_LIMITS, type SectionElement } from "@chaiberi/shared";

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { DeviceValueRow } from "../fields/DeviceValueRow";
import { useSectionForm } from "../section-form-context";
import { setElementSpace } from "../../../../lib/site-builder/element-place";
import { formatPx } from "../../../../lib/site-builder/look-space";

type Side = "top" | "bottom";
type Space = NonNullable<SectionElement["space"]>;

const PX = { factor: 1, decimals: 2 } as const;
/** Ступени-подсказки при «Обычно»: прежние 0 / XS / S / M / L. */
const STEPS = [0, 8, 16, 32, 64] as const;

const PHONE_KEY: Readonly<Record<Side, "topPhone" | "bottomPhone">> = { top: "topPhone", bottom: "bottomPhone" };

/** Короткая подпись сброса — длинная выталкивала кнопку за край панели. */
export const ELEMENT_SPACE_INHERIT = "обычно";
/** Подсказка под полем: что значит «обычно» и что делает своё число. */
const elementSpaceHint = (rhythm: number): string => `Обычно — только ритм колонки (${formatPx(rhythm)} px); своё число добавляется к нему.`;

interface Props {
  side: Side;
  label: string;
  space: Space | undefined;
  onChange: (space: Space | undefined) => void;
  /** Путь элемента — живой предпросмотр `…space.top`. */
  path: string;
  /** Ритм колонки на ПК, px — к нему добавляется отступ (подпись «обычно»). */
  rhythm: number;
  /** Плотность магазина (ступени-подсказки) и доля телефона темы. */
  density: number;
  phoneFactor: number;
}

export function ElementSpaceField({ side, label, space, onChange, path, rhythm, density, phoneFactor }: Props) {
  const { preview } = useSectionForm();
  const phoneKey = PHONE_KEY[side];
  const desktop = space?.[side];
  const ownPhone = space?.[phoneKey];
  const phoneAuto = (desktop ?? 0) * phoneFactor;
  const summary = ownPhone !== undefined ? `на телефоне своё: ${formatPx(ownPhone)} px` : `на телефоне ${formatPx(phoneAuto)} px (${Math.round(phoneFactor * 100)} %)`;
  const range = SECTION_LOOK_LIMITS.elementSpace;
  const presets = STEPS.map((px) => {
    const value = Math.round(px * density * 10) / 10;
    return { label: formatPx(value), value };
  });

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <NumberSliderField
        label={label}
        value={desktop}
        inherit={{ value: 0, label: ELEMENT_SPACE_INHERIT }}
        min={range.min}
        max={range.max}
        step={1}
        largeStep={8}
        unit="px"
        display={PX}
        presets={presets}
        hint={elementSpaceHint(rhythm)}
        onChange={(v) => onChange(setElementSpace(space, side, v))}
        onPreview={(v) => preview(`${path}.space.${side}`, v)}
      />
      <DeviceValueRow summary={summary} own={ownPhone !== undefined} label={`${label} на телефоне`}>
        <NumberSliderField
          label={`${label} на телефоне`}
          value={ownPhone}
          inherit={{ value: Math.round(phoneAuto * 100) / 100, label: "по доле" }}
          min={range.min}
          max={range.max}
          step={1}
          unit="px"
          display={PX}
          onChange={(v) => onChange(setElementSpace(space, phoneKey, v))}
          onPreview={(v) => preview(`${path}.space.${phoneKey}`, v)}
        />
      </DeviceValueRow>
    </div>
  );
}
